import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import { forwardRef } from 'react'
import { Dropdown, DropdownProps } from 'shared/components/Dropdown/Dropdown'
import { Text } from 'shared/components/Text/Text'
import { primaryBlack, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { FlexBox } from '../../../components/Base/FlexBox'
import {
  ProfileJobOrderBy,
  ProfileJobOrderByAscDescSort,
  ProfileJobOrderByDirection,
  profileJobOrderByLabels,
} from './ProfileJobs'

interface ProfileJobOrderByDropownProps {
  orderBy: ProfileJobOrderBy
  orderByDirection: ProfileJobOrderByDirection
  onChange: (orderBy: ProfileJobOrderBy, orderByDirection: ProfileJobOrderByDirection) => void
  isDisabled?: boolean
  align: 'left' | 'right'
}

const Value = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${royalBlue};
    position: relative;
    font-size: 24px;
    top: -1px;
    left: -5px;
  }

  svg:hover {
    color: ${primaryBlack};
  }
`

export const ProfileJobOrderByDropown = forwardRef(function ProfileJobOrderByDropown(
  {
    orderBy,
    orderByDirection,
    onChange,
    isDisabled,
    align,
    ...rest
  }: ProfileJobOrderByDropownProps & Omit<DropdownProps<unknown>, 'options' | 'onChange'>,
  ref: any
) {
  function renderDropDownLabel(option: ProfileJobOrderBy) {
    if (ProfileJobOrderByAscDescSort.includes(option) && orderBy === option) {
      return (
        <FlexBox alignItems="center" gap="10px" color={royalBlue}>
          <span>{profileJobOrderByLabels[option]}</span>{' '}
          {orderByDirection === '-' ? (
            <ArrowDownwardIcon fontSize="small" />
          ) : (
            <ArrowUpwardIcon fontSize="small" />
          )}
        </FlexBox>
      )
    }
    return (
      <FlexBox alignItems="center" gap="10px" color={orderBy === option ? royalBlue : primaryBlack}>
        {profileJobOrderByLabels[option]}
      </FlexBox>
    )
  }

  const options = [
    {
      value: ProfileJobOrderBy.PICK_UP_DATE,
      label: ProfileJobOrderBy.PICK_UP_DATE,
      dropDownLabel: renderDropDownLabel(ProfileJobOrderBy.PICK_UP_DATE),
    },
    {
      value: ProfileJobOrderBy.DROPOFF_DATE,
      label: ProfileJobOrderBy.DROPOFF_DATE,
      dropDownLabel: renderDropDownLabel(ProfileJobOrderBy.DROPOFF_DATE),
    },
    {
      value: ProfileJobOrderBy.STATUS,
      label: ProfileJobOrderBy.STATUS,
      dropDownLabel: renderDropDownLabel(ProfileJobOrderBy.STATUS),
    },
    {
      value: ProfileJobOrderBy.BROKER,
      label: ProfileJobOrderBy.BROKER,
      dropDownLabel: renderDropDownLabel(ProfileJobOrderBy.BROKER),
    },
    {
      value: ProfileJobOrderBy.DATE_LAST_MODIFIED,
      label: ProfileJobOrderBy.DATE_LAST_MODIFIED,
      dropDownLabel: renderDropDownLabel(ProfileJobOrderBy.DATE_LAST_MODIFIED),
    },
  ]

  const renderOpener = () => {
    return (
      <Value>
        <FlexBox gap="10px" alignItems="center">
          <Text variant="header3" fontColor={isDisabled ? primaryBlack : royalBlue}>
            Sort by {profileJobOrderByLabels[orderBy]?.toLocaleLowerCase()}
          </Text>
          {orderByDirection === '-' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
          {!isDisabled ? <ArrowDropDownIcon /> : null}
        </FlexBox>
      </Value>
    )
  }

  function handleChange(val: unknown) {
    const newOrderBy = val as ProfileJobOrderBy
    const newOrderByDirection =
      ProfileJobOrderByAscDescSort.includes(newOrderBy) && orderBy === newOrderBy && orderByDirection === '-'
        ? ''
        : '-'

    onChange(val as ProfileJobOrderBy, newOrderByDirection)
  }

  return (
    <Dropdown
      ref={ref}
      {...rest}
      onChange={handleChange}
      noMargin
      isDisabled={isDisabled}
      options={options}
      renderOpener={renderOpener}
      maxHeight={450}
      align={align}
      width="250px"
    />
  )
})
